/**
 * DOCUMENTATION:
 * Component: SystemDesignCaching
 * Purpose: Teaching caching layers, Redis/Memcached, and eviction policies.
 * * * Note for Junior Developers:
 * - Cache Hit: The data was found in the cache. Fast!
 * - Cache Miss: The data was NOT in the cache. We must go to the DB. Slow.
 * - TTL (Time to Live): How long data stays in the cache before it expires.
 * - Invalidation: The hardest part of caching. When the DB changes, the cache
 * must be updated or deleted ("The two hard things: naming and cache invalidation").
 * * FIX: Replaced raw LaTeX string with react-katex to solve ESLint 'no-undef' errors.
 */

import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
} from "@mui/material";

/* ✅ Math Rendering Imports */
import 'katex/dist/katex.min.css';
import { BlockMath } from 'react-katex';

/* Icons */
import BoltIcon from '@mui/icons-material/Bolt';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.9), rgba(10, 5, 25, 0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(20, 15, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    color: "white",
}));

const StrategyBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: "rgba(0, 200, 255, 0.03)",
    border: "1px solid rgba(0, 200, 255, 0.15)",
    borderRadius: "10px",
    height: "100%",
}));

export default function SystemDesignCaching() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const strategies = [
        {
            name: "Read-Through",
            desc: "The application asks the cache for data. If it's a miss, the cache fetches it from the DB and updates itself."
        },
        {
            name: "Write-Through",
            desc: "Data is written to the cache and the database at the same time. Ensures the cache is never stale."
        },
        {
            name: "Write-Back",
            desc: "Data is written to the cache first; the DB is updated later. Extreme performance, but risk of data loss if the cache crashes."
        },
    ];

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "#00c8ff" }}>
                    CACHING STRATEGIES
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 1. The Caching Hierarchy */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#00c8ff" }}><BoltIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>Where do we cache?</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            Caching can happen at every level of the stack. A request might hit a <strong>Browser Cache</strong>,
                            then a <strong>CDN (Edge)</strong>, then an <strong>Application Cache (Redis)</strong>,
                            and finally a <strong>Database Buffer</strong>.
                        </Typography>



                        <Typography variant="h6" sx={{ mt: 4, mb: 2, color: "#00c8ff" }}>Write Strategies</Typography>



                        <Grid container spacing={2}>
                            {strategies.map((s, i) => (
                                <Grid item xs={12} md={4} key={i}>
                                    <StrategyBox>
                                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>{s.name}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>{s.desc}</Typography>
                                    </StrategyBox>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. Eviction Policies */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#ff6b6b" }}><DeleteSweepIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>Eviction: Making Room</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            Cache memory is expensive and limited. When the cache is full, we must decide what to delete.
                            This is called an <strong>Eviction Policy</strong>.
                        </Typography>

                        <Stack spacing={2}>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography color="#00c8ff" fontWeight={700}>LRU (Least Recently Used)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>Discard the data that hasn't been accessed for the longest time. Most common in Redis.</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography color="#00c8ff" fontWeight={700}>LFU (Least Frequently Used)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>Count how often data is accessed. Discard the ones with the lowest count.</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography color="#00c8ff" fontWeight={700}>FIFO (First In, First Out)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>The oldest items are evicted first, regardless of how often they are used.</Typography>
                            </Box>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* 3. The Performance Impact */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="#00c8ff" mb={2}>Calculating Hit Rate</Typography>
                        <Typography variant="body2" mb={3} sx={{ opacity: 0.8 }}>
                            The efficiency of your cache is measured by the <strong>Cache Hit Ratio</strong>.
                            A ratio of 0.95 means 95% of your requests never hit the slow database.
                        </Typography>

                        <Box sx={{ p: 3, bgcolor: "rgba(0,0,0,0.3)", borderRadius: "8px", textAlign: "center", color: 'goldenrod' }}>
                            {/* ✅ FIXED: BlockMath uses a string prop to avoid JSX variable parsing errors */}
                            <BlockMath math="\text{Hit Ratio} = \frac{Hits}{Hits + Misses}" />
                        </Box>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/system-design-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#00c8ff", color: "#0a0519", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
